import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "@/components/Reveal";

const PROGRAMS = [
  {
    id: "the-chaos-audit",
    name: "The Chaos Audit",
    format: "One week · 1:1 intensive",
    price: "$1,800",
    blurb:
      "A forensic week inside your business. You leave with a one-page map of what to kill, what to keep, and the exact order to fix it.",
    points: ["Full business & energy audit", "One-page simplification plan", "90-minute debrief call"],
    dark: false,
    span: "lg:col-span-7",
  },
  {
    id: "systems-sprint",
    name: "Systems Sprint",
    format: "Six weeks · Small cohort",
    price: "$2,400",
    blurb:
      "Build the five core systems every calm business runs on: delivery, sales, money, weekly rhythm, and delegation.",
    points: ["Weekly live working sessions", "Templates you'll actually use", "Max 12 founders per cohort"],
    dark: true,
    span: "lg:col-span-5 lg:mt-20",
  },
  {
    id: "scale-calm-mentorship",
    name: "Scale Calm Mentorship",
    format: "Six months · 1:1 ongoing",
    price: "$1,200/mo",
    blurb:
      "For founders ready to grow without the grind. Twice-monthly calls, unlimited async access, and someone in your corner who protects your calendar as fiercely as your revenue.",
    points: ["2 calls per month", "Unlimited async access", "Quarterly planning intensives"],
    dark: false,
    span: "lg:col-span-6 lg:col-start-4",
  },
];

export default function Programs() {
  return (
    <main data-testid="programs-page" className="pt-32 sm:pt-44">
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-24 sm:pb-32">
        <Overline className="mb-10">Programs</Overline>
        <h1 className="font-serif text-5xl sm:text-7xl font-light tracking-tighter leading-[0.95] text-ink max-w-4xl">
          <MaskedLine delay={0.2}>Three ways in.</MaskedLine>
          <MaskedLine delay={0.35} className="italic text-sage">
            One way out of chaos.
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 text-base sm:text-lg leading-relaxed text-neutral-600 max-w-xl"
        >
          Every engagement starts with subtraction, not addition. Pick the depth that
          matches where your business — and your nervous system — are right now.
        </motion.p>
      </section>

      <section data-testid="programs-list-section" className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-32 sm:pb-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {PROGRAMS.map((p, i) => (
            <FadeUp key={p.id} delay={0.08 * i} className={p.span}>
              <article
                data-testid={`program-card-${p.id}`}
                className={`group h-full p-10 sm:p-14 flex flex-col transition-colors duration-500 ${
                  p.dark
                    ? "bg-sage text-sand hover:bg-ink"
                    : "bg-bone border border-border hover:border-sage"
                }`}
              >
                <div className="flex items-start justify-between gap-6 mb-10">
                  <p className={`text-xs uppercase tracking-[0.25em] ${p.dark ? "text-sand/60" : "text-neutral-500"}`}>
                    {p.format}
                  </p>
                  <p className={`font-serif text-2xl font-light ${p.dark ? "text-clay" : "text-sage"}`}>
                    {p.price}
                  </p>
                </div>
                <h2 className="font-serif text-3xl sm:text-5xl font-light mb-6">{p.name}</h2>
                <p className={`leading-relaxed mb-10 ${p.dark ? "text-sand/70" : "text-neutral-600"}`}>
                  {p.blurb}
                </p>
                <ul className="flex flex-col gap-3 mb-12">
                  {p.points.map((pt) => (
                    <li key={pt} className={`flex items-center gap-3 text-sm ${p.dark ? "text-sand/80" : "text-neutral-600"}`}>
                      <Check className="w-4 h-4 text-clay shrink-0" strokeWidth={1.5} />
                      {pt}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/contact?program=${encodeURIComponent(p.name)}`}
                  data-testid={`program-enquire-${p.id}`}
                  className={`group/link mt-auto w-fit flex items-center gap-3 text-sm tracking-wide rounded-full px-7 py-3.5 transition-colors duration-300 ${
                    p.dark
                      ? "bg-sand text-ink hover:bg-clay"
                      : "bg-sage text-sand hover:bg-ink"
                  }`}
                >
                  Enquire about {p.name}
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-300" />
                </Link>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>
    </main>
  );
}
